import React, { useEffect, useState } from 'react';
import { EmptySearch, StyledNetworkBase, Wrapper } from './link-administrators.styles';
import { Search } from '@vtbhome/juicyfront';
import { useUsersListSharedQuery } from '@entities/user/libs/hooks/use-users-list-shared-query/use-users-list-shared-query';
import { DEFAULT_USERS_LIST_PARAMETERS } from '@entities/user/config/store-constants';
import { debounce } from '@vtbhome/juicyfront/utils/helpers';
import { Typography } from '@shared/ui/typography/typography';
import { AllOtherPeople } from '@vtbhome/juicyfront/indexIcon';
import { userName } from '@shared/lib/utils/user-name/user-name';
import { LinkAdministratorsProps } from './types';
import { NetworkInitialLoader } from '@shared/ui/network-state-new/network-initial-loader/network-initial-loader';
import { UserListItem, UserRoles } from '@shared/model/user';
import { ActionListUser } from '@entities/user/ui/action-list-user/action-list-user';
import { FeedbackSubjectUser } from '@shared/model/feedback-subject';

export const LinkAdministrators: React.FC<LinkAdministratorsProps> = ({ value = [], onChange }) => {
    const [usersListParameters, setUsersListParameters] = useState({
        ...DEFAULT_USERS_LIST_PARAMETERS,
        role: UserRoles.Admin,
        take: 10,
    });
    const [search, setSearch] = useState(usersListParameters.search);
    const [getUsers, getUsersResult, , updateQueryData] = useUsersListSharedQuery(usersListParameters);
    const searchUsers = getUsersResult.data?.items;

    const isLoading = getUsersResult.isLoading || getUsersResult.isFetching;
    const isEmpty = !searchUsers?.length && !usersListParameters.search && !value.length && !isLoading;
    const isEmptySearch = !searchUsers?.length && usersListParameters.search && !value.length && !isLoading;

    const filterSearchUsersQueryData = (user: number | number[], result?: UserListItem[]) =>
        updateQueryData({
            items: result?.filter((queryUser) =>
                Array.isArray(user) ? !user.includes(queryUser.id) : queryUser.id !== user
            ),
        });

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target as HTMLInputElement | null;
        if (!input) {
            return;
        }
        setUsersListParameters((prev) => ({ ...prev, search: input.value }));
        setSearch(input.value);
    };

    const handleClearSearch = () => {
        setUsersListParameters((prev) => ({ ...prev, search: '' }));
        setSearch('');
    };

    const handleAddUser = (user: FeedbackSubjectUser) => () => {
        onChange([...value, user]);
        filterSearchUsersQueryData(user.id, searchUsers);
        handleClearSearch();
    };

    const handleDeleteUser = (user: FeedbackSubjectUser) => () => {
        const newUsersList = value.filter((selectedUser) => selectedUser.id !== user.id);
        onChange(newUsersList);
    };

    const handleSearchDebounce = debounce(handleSearch, 300);

    useEffect(() => {
        usersListParameters.search &&
            getUsers(usersListParameters).then((result) =>
                filterSearchUsersQueryData(
                    value.map((user) => user.id),
                    result.data?.items
                )
            );
    }, [usersListParameters]);

    return (
        <Wrapper>
            <Search
                placeholder='Введите ФИО для поиска и добавления'
                value={search}
                onChange={handleSearchDebounce}
                onClear={handleClearSearch}
            />
            {isLoading && (
                <NetworkInitialLoader
                    style={{
                        minHeight: 212,
                    }}
                />
            )}
            {isEmpty && (
                <StyledNetworkBase
                    Icon={<AllOtherPeople />}
                    size='s'
                    description='Если не привязать администраторов, то тема будет привязана ко всем администраторам'
                />
            )}
            {isEmptySearch && (
                <EmptySearch>
                    <Typography size='body1_short' color='neutral_50'>
                        По вашему запросу ничего не найдено
                    </Typography>
                </EmptySearch>
            )}
            {!isLoading &&
                usersListParameters.search &&
                searchUsers?.map((user) => {
                    const name = userName({
                        firstName: user.firstName,
                        middleName: user.middleName,
                        lastName: user.lastName,
                    }).full;
                    return (
                        <ActionListUser
                            key={user.id}
                            name={name}
                            photoUrl={user.photoUrl}
                            jobTitle={user.jobTitle}
                            onAction={handleAddUser(user)}
                        />
                    );
                })}
            {value.map((user) => {
                const name = userName({
                    firstName: user.firstName,
                    middleName: user.middleName,
                    lastName: user.lastName,
                }).full;
                return (
                    <ActionListUser
                        key={user.id}
                        action='delete'
                        name={name}
                        photoUrl={user.photoUrl}
                        jobTitle={user.jobTitle}
                        onAction={handleDeleteUser(user)}
                    />
                );
            })}
        </Wrapper>
    );
};
